import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  EquipmentId,
  type CleaningRecordId,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { CleaningPropertyList } from "@/features/cleaning-form";
import { ArrowLeftIcon, CheckIcon } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/inventory/equipment/$equipment_id/cleaning/$record_id",
)({
  component: CleaningRecordPage,
  params: z.object({
    equipment_id: z.coerce.number().transform((val) => val as EquipmentId),
    record_id: z.coerce
      .number()
      .transform((val) => val as CleaningRecordId),
  }),
});

function CleaningRecordPage() {
  const { equipment_id, record_id } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: record } =
    trpc.cleaning.records.getWithValues.useQuery(record_id);

  const completeMutation = trpc.cleaning.records.update.useMutation({
    onSuccess: () => {
      toast.success("Cleaning record completed");
      utils.cleaning.records.getWithValues.invalidate(record_id);
      utils.cleaning.records.list.invalidate({ equipmentId: equipment_id });
    },
    onError: (err) => toast.error(`Failed to complete: ${err.message}`),
  });

  const deleteMutation = trpc.cleaning.records.delete.useMutation({
    onSuccess: () => {
      toast.success("Cleaning record deleted");
      utils.cleaning.records.list.invalidate({ equipmentId: equipment_id });
      navigate({
        to: "/inventory/equipment/$equipment_id/cleaning",
        params: { equipment_id },
      });
    },
    onError: (err) => toast.error(`Failed to delete: ${err.message}`),
  });

  return (
    <div className="flex-1 p-6 space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate({
              to: "/inventory/equipment/$equipment_id/cleaning",
              params: { equipment_id },
            })
          }
          className="text-muted-foreground"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Cleaning
        </Button>
        <DeleteConfirmDialog
          description="Delete this cleaning record? This cannot be undone."
          onConfirm={() => deleteMutation.mutate(record_id)}
          isDeleting={deleteMutation.isPending}
        />
      </div>

      <div className="flex items-center gap-2">
        <h2 className="text-sm font-medium">
          {record?.completed_at ? "Cleaning Record" : "Edit Cleaning Record"}
        </h2>
        {record?.completed_at && (
          <Badge variant="default" className="gap-1 text-xs">
            <CheckIcon className="h-3 w-3" />
            Complete
          </Badge>
        )}
      </div>

      <CleaningPropertyList
        recordId={record_id}
        equipmentId={equipment_id}
        isEditable={!record?.completed_at}
      />

      {record && !record.completed_at && (
        <Button
          className="w-full"
          onClick={() =>
            completeMutation.mutate({
              id: record_id,
              completedAt: new Date(),
            })
          }
          disabled={completeMutation.isPending}
        >
          <CheckIcon className="h-4 w-4 mr-2" />
          Complete Cleaning Record
        </Button>
      )}
    </div>
  );
}
